import { forwardRef, useCallback } from 'react'
import { Button, ButtonProps, Link, LinkProps } from '@mui/material'
import { NavigateOptions, useNavigate } from 'react-router-dom'

type LoadedLinkProps = {
  to: string
  layout?: 'link' | 'button'
  openNewTab?: boolean
  isExternalUrl?: boolean
  navigationOptions?: NavigateOptions
  handleClick?: (event) => void
} & (LinkProps | ButtonProps)

const LoadedLink = forwardRef<HTMLDivElement, LoadedLinkProps>(
  (
    {
      children,
      to,
      layout = 'link',
      openNewTab = false,
      isExternalUrl = false,
      navigationOptions,
      handleClick,
      ...props
    },
    ref
  ) => {
    const navigate = useNavigate()

    const onClick = useCallback(
      (event) => {
        handleClick?.(event)

        let shouldUseNewTab = openNewTab

        // If the use is ctrl/command clicking, middle clicking, or the
        // prop forcing a new tab, open in a new tab
        if (event.ctrlKey || event.metaKey || event.button == 2) {
          shouldUseNewTab = true
        }

        if (!shouldUseNewTab) {
          window.dispatchEvent(new CustomEvent('@@loaded/reactNavigation'))
          navigate(to, navigationOptions)
        } else {
          if (isExternalUrl) {
            window.open(to, '_blank', 'noopener,noreferrer')
          } else {
            window.open(
              `${window.location.origin}${window.location.pathname}#${to}`,
              '_blank',
              'noopener,noreferrer'
            )
          }
        }
      },
      [handleClick, openNewTab, navigate, to, navigationOptions, isExternalUrl]
    )

    return layout === 'link' ? (
      <Link
        component="button"
        underline="none"
        onClick={onClick}
        {...(props as LinkProps)}
        ref={ref}
      >
        {children}
      </Link>
    ) : (
      <Button
        onClick={onClick}
        variant="contained"
        color="info"
        {...(props as ButtonProps)}
      >
        {children}
      </Button>
    )
  }
)

LoadedLink.displayName = 'LoadedLink'

export default LoadedLink
